<template>
    <v-combobox
        outlined
        v-model="internalItems"
        :hide-details="validate()"
        item-text="name"
        item-value="id"
        :items="sortedItems"
        :disabled="disabled"
        :label="label"
        :loading="loading"
        multiple
        chips
        deletable-chips
        :no-data-text="noDataText"
        :rules="[
            () =>
                validate() ||
                `A maximum of ${maxLength} tags are allowed.`,
        ]"
        :maxLength="20"
        return-object
        :menu-props="computedMenuProps"
        v-test="'tags-combobox'"
        class="w-100 minimize-height"
        @change="handleChange"
    >
        <template v-slot:selection="{ parent, item }">
            <Chip :label="item" :closable="disabled ? false : true" @click:close="parent.selectItem(item)"></Chip>
        </template>
    </v-combobox>
</template>
<script lang="ts">
import { type Label } from '@/types/Label';
import api from "@/api";
import ResponseArray from '@/types/ResponseArray';
import Chip from "@/components/tags/DisplayChip.vue";
import { getRandomTagColor } from "@/helpers/tags";
import DeviceUtility from '@/helpers/device';

export default {
  name: 'ComboBox',
  props: {
    value: {
      type: Array as () => Label[],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "Tags (Optional)",
    },
    maxLength: {
      type: Number,
      default: 5,
    },
  },
  components: {
    Chip,
  },
  data() {
    return {
      internalItems: [] as Label[],
      items: [] as Label[],
      loading: true,
      currentPage: 1,
      itemsPerPage: 9999,
      itemsOnServer: 0,
      noDataText: 'No tags found.',
      maximumNumberOfTags: this.maxLength
    };
  },
  beforeMount() {
    this.loadTags();
  },
  mounted() {
    if (this.isMobile) {
      /* Monkey Patch: for iOS Safari and other mobile browsers to provide a better experience in large growing comboboxes. */
      const inputEl = this?.$children?.[0]?.$el?.querySelector('input')
      if (inputEl) {
        inputEl.readOnly = true
        inputEl.setAttribute('inputmode', 'none')
        inputEl.setAttribute('autocomplete', 'off')
        inputEl.setAttribute('autocorrect', 'off')
        inputEl.setAttribute('autocapitalize', 'off')
        inputEl.setAttribute('spellcheck', 'false')
        inputEl.addEventListener('focus', e => {
          e.target.blur()
        })
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.internalItems = newVal
          .filter((value: Label | string) =>
            typeof value !== 'string' || value.trim() !== ''
          )
          .map(this.stringToTag)
          .sort((a : Label, b : Label) => a.name.localeCompare(b.name));
      }
    },
    internalItems: {
      handler(newVal) {
        // Filter out empty or space-only items first
        const filteredItems = newVal.filter((item: Label | string) => this.filterLabelNamesWithoutText(item));
        
        // Only continue processing if we have valid items
        if (JSON.stringify(filteredItems) !== JSON.stringify(newVal)) {
          // If we filtered something out, update internalItems without triggering the watcher again
          this.$nextTick(() => {
            this.internalItems = filteredItems;
          });
          return; // Exit early to avoid processing invalid data
        }
        
        // Process valid items
        const mapped = filteredItems
          .map(this.stringToTag)
          .sort((a: Label, b: Label) => a.name.localeCompare(b.name));
          
        if (JSON.stringify(mapped) !== JSON.stringify(this.value)) {
          this.$emit('input', mapped);
          this.$emit('change', mapped);
        }
      },
      deep: true
    },
  },
  methods: {
    handleChange(items : Array<Label | string>) {
      // Immediately filter out any space-only values when the user adds something
      if (Array.isArray(items)) {
        const filteredItems = items.filter((item) => this.filterLabelNamesWithoutText(item));
        
        if (filteredItems.length !== items.length) {
          // We filtered something out, update the model
          this.internalItems = filteredItems as Label[];
        }
      }
    },
    filterLabelNamesWithoutText(item: string | Label) {
       if (typeof item === 'string') {
            return item.trim() !== '';
        }
        return item?.name?.trim() !== '';
    },
    async loadTags() {
      this.loading = true;
      try {
        const resp = await (api as any).getLabels({
          page: this.currentPage,
          pageSize: this.itemsPerPage,
          where: {},
          order: [["createdAt", "DESC NULLS FIRST"]],
        }) as ResponseArray<Label>;
        this.itemsOnServer = resp.count;
        this.items = resp.rows;
      } catch (err) {
        console.error("Error loading tags", err);
        this.noDataText = "There was an issue loading your organization's tags. Please refresh the page to try again.";
      } finally {
        this.loading = false;
      }
    },
    // This saves an API hit when conversations or media or projects save. I can just used the returned value and replace it in my list. 
    replaceSavedTags(tags: Label[]) {
      this.items = this.items.map(item => {
        if (!item.id) {
          const tag = tags.find(tag => tag.name === item.name);
          if (tag) {
            return tag;
          }
          return item;
        }
        return item;
      });
    },
    validate() {
        return this.internalItems.length <= this.maximumNumberOfTags
    },
    stringToTag(v: string | Label): Object {
      if (typeof v === 'string') {       
        // Check if already exists in items
        const existing = this.items.find(i => i.name.toLowerCase() === v.toLowerCase());
        if (!existing) {
          // If not, create a new tag object
          const newTag = { name: v, baseColor: getRandomTagColor() } as Label;
          if (!this.items.some(x => x.name === v)) {
            this.items.push(newTag);
          }
          return newTag;
        }
        return existing;
      }
      return v;
    },
    sortItems(a: Label, b: Label) {
        const aSelected = this.internalItems.some(item => 
          typeof item === 'object' ? item.id === a.id : item === a
        );
        const bSelected = this.internalItems.some(item => 
          typeof item === 'object' ? item.id === b.id : item === b
        );
        
       // First sort by selection status
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      return a.name.localeCompare(b.name);
    }
  },
  computed: {
    // Sort items to put selected ones at the top
    sortedItems() {
      return [...this.items].sort(this.sortItems);
    },
    isMobile() {
      return DeviceUtility.isMobile;
    },
    computedMenuProps() {
      return { zIndex: 9999 };
    },
  },
}
</script>